import { styled, css } from 'styled-components';

export const Container = styled.div`
  @media print {
    display: none !important;
  }
`;

export const Display = styled.div<{ $isCollapsed: boolean }>`
  ${({ theme: { borderRadius, colors, fontSizes, fontWeights, motionSpeeds, motions }, $isCollapsed }) => css`
    align-items: center;
    box-sizing: border-box;
    border-radius: ${borderRadius.sm};
    color: ${colors.brandDark};
    cursor: pointer;
    display: flex;
    gap: 0.5rem;
    height: 2rem;
    justify-content: center;
    outline: 2px solid transparent;
    transition: all ${motionSpeeds.fast} ${motions.default};
    width: 2rem;

    .icon {
      flex-shrink: 0;
      height: 1rem;
      width: 1rem;
    }

    ${
      $isCollapsed &&
      css`
        .display-chevron {
          transform: scaleY(-1);
        }
      `
    }

    span {
      display: block;
      font-size: ${fontSizes.s14};
      font-weight: ${fontWeights.medium};
    }

    &:hover {
      background: ${colors.brandDarkAlpha5};
    }

    &:focus-within,
    &:focus {
      background: ${colors.brandDarkAlpha5};
      outline-color: ${colors.brandPrimaryAlpha50};
      outline-offset: 2px;
    }

    * {
      user-select: none;
    }
  `}
`;

export const Divider = styled.div`
  width: calc(100% + 2rem);
  height: 1px;
  margin-left: -1rem;
  background: ${({ theme: { colors } }) => colors.brandDarkTint80};
`;

export const Content = styled.div`
  ${({ theme: { borderRadius, colors } }) => css`
    background: ${colors.brandLight};
    border-radius: ${borderRadius.sm};
    box-shadow:
      0 0 0 1px ${colors.brandDarkTint80},
      0 0.75rem 0.5rem -0.25rem rgba(32, 30, 36, 0.15),
      0 0.25rem 0.25rem -0.125rem rgba(32, 30, 36, 0.2);
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1.5rem 1rem 1rem;
    user-select: none;
    width: 15rem;
  `}
`;

export const Item = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  gap: 1rem;
  width: 100%;

  label {
    margin: 0;
  }
`;

export const ExportButton = styled.button`
  ${({ theme: { colors } }) => css`
    align-items: center;
    background: transparent;
    border: 0;
    border-radius: 0.25rem;
    color: ${colors.brandDark};
    cursor: pointer;
    display: flex;
    font-weight: 500;
    flex: 1;
    gap: 1rem;
    margin: -0.25rem -0.5rem;
    padding: 0.5rem;
    user-select: none;

    &:hover {
      background: ${colors.brandDarkAlpha5};
    }
  `}
`;

export const ItemLabel = styled.span`
  ${({ theme: { fontSizes, fontWeights } }) => css`
    flex: 1;
    flex-shrink: 0;
    font-size: ${fontSizes.s14};
    font-weight: ${fontWeights.medium};
  `}
`;

export const List = styled.div`
  ${({ theme: { borderRadius, colors } }) => css`
    background: ${colors.brandLight};
    border-radius: ${borderRadius.sm};
    box-shadow:
      0 0 0 1px ${colors.brandDarkTint80},
      0 0.75rem 0.5rem -0.25rem rgba(32, 30, 36, 0.15),
      0 0.25rem 0.25rem -0.125rem rgba(32, 30, 36, 0.2);
    display: flex;
    flex-direction: column;
    min-width: calc(50% + 0.35rem);
    padding: 0.5rem;
    position: absolute;
    right: 0;
    top: calc(100% + 0.25rem);
    z-index: 1;
  `}
`;

export const ListDisplay = styled.div<{ $isCollapsed: boolean }>`
  ${({ theme: { borderRadius, colors, motionSpeeds, motions }, $isCollapsed }) => css`
    align-items: center;
    border: 1px solid ${colors.brandDarkTint80};
    border-radius: ${borderRadius.sm};
    box-sizing: border-box;
    color: ${colors.brandDark};
    cursor: pointer;
    display: flex;
    flex: 1.5;
    flex-shrink: 0;
    gap: 0.5rem;
    justify-content: space-between;
    outline: 2px solid transparent;
    padding: 0.25rem 0.5rem 0.25rem 0.75rem;
    transition: all ${motionSpeeds.fast} ${motions.default};

    .display-chevron {
      flex-shrink: 0;
      height: 1rem;
      width: 1rem;

      ${
        $isCollapsed &&
        css`
          transform: scaleY(-1);
        `
      }
    }

    &:focus-within,
    &:focus {
      background: ${colors.brandDarkAlpha5};
      outline-color: ${colors.brandPrimaryAlpha50};
      outline-offset: 2px;
    }
  `}
`;

export const ListItem = styled.button`
  ${({ theme: { borderRadius, colors, fontSizes, motionSpeeds, motions } }) => css`
    all: unset;
    align-items: center;
    border-radius: ${borderRadius.sm};
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    outline: 2px solid transparent;
    padding: 0.25rem;
    transition: all ${motionSpeeds.fast} ${motions.default};

    span {
      display: block;
      font-size: ${fontSizes.s14};
    }

    .icon {
      height: 1rem;
      width: 1rem;
    }

    &:hover {
      background: ${colors.brandPrimaryAlpha5};
    }

    &:focus-within,
    &:focus {
      outline-color: ${colors.brandPrimaryAlpha50};
      outline-offset: 2px;
    }
  `}
`;

export const Stepper = styled.div`
  align-items: center;
  display: flex;
  gap: 0.5rem;
`;

export const StepperValue = styled.span`
  ${({ theme: { fontSizes, fontWeights } }) => css`
    font-size: ${fontSizes.s14};
    font-weight: ${fontWeights.medium};
    min-width: 1rem;
    text-align: center;
  `}
`;

export const VisuallyHidden = styled.span`
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;
